import React, { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormGroup,  
  FormControlLabel,
  Grid,  
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CircleIcon from "@mui/icons-material/Circle";
import Stack from "@mui/material/Stack";

import { getJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";

import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import ViewLocationCard from "./FormComponents/ViewLocationCard";
import PageOneReviewSection from "./FormComponents/PageOneReviewSection";
import PaymentDetailsSection from "./FormComponents/PaymentDetailsSection";
import TechnicianOnlyReviewSection from "./FormComponents/TechnicianOnlyReviewSection";
import ClientOnlyReviewSection from "./FormComponents/ClientOnlyReviewSection";
import DocumentLinksCard from "./FormComponents/DocumentLinksCard";
import SectionTimestampNotice from "./FormComponents/SectionTimestampNotice";
import { buildGeneratedDocumentLinks } from "./FormComponents/email/buildGeneratedDocumentLinks";
import { getEffectiveEndTime } from "./utils/timeFormatter";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

const PEST_OPTIONS = ["Roaches", "Mosq", "Rodents", "Flies", "Ants", "Others"];

function ViewMakatiGpcForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState({});
  const [client, setClient] = useState({});
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPayrollReports = searchParams.get('from') === 'payroll-reports';
  const returnTo = searchParams.get('returnTo');

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client || {});
        setJobOrder(data || {});
      } catch (err) {
        setErrorMessage(
          `Error fetching job order${err?.message ? `: ${err.message}` : ""}`
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobOrder(); // Fetch the job order if the ID exists
  }, []); // Dependency array ensures the effect runs when ID changes

  // helper to normalize labels into keys
  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  // map raw keys back to label arrays and sort alphabetically
  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) => (jobOrder.job_description || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));
  
  // ——————— View-only Inspection data ———————
  const locations = jobOrder.inspection_report || [];

  // parse all pest-counts as numbers and sum per type
  const overallTotals = PEST_OPTIONS.reduce((acc, pest) => {
    acc[pest] = locations.reduce(
      (sum, loc) => sum + (Number(loc.pests?.[pest]) || 0),
      0
    );
    return acc;
  }, {});

  const grandTotal = Object
    .values(overallTotals)
    .reduce((sum, n) => sum + n, 0);

  const effectiveEndTime = getEffectiveEndTime(jobOrder);

  const displayJobOrder = {
    ...jobOrder,
    end_time: effectiveEndTime,
  };

  const pageOneJobOrder = {
    ...displayJobOrder,
    client: displayJobOrder?.client || client || {},
  };

  const STEP_LABELS = {
    review_job_details: "Review Job Details Completed At",
    inspection_report: "Inspection Report Completed At",
    service_details: "Service Details Completed At",
    payment_details: "Payment Details Completed At",
    technician_confirmation: "For Technicians Only Completed At",
    client_signature: "For Client Only - Signature Completed At",
    client_satisfaction: "For Client Only - Satisfaction Completed At",
  };

  const getLatestStepLogMap = (logs = []) => {
    return (Array.isArray(logs) ? logs : []).reduce((acc, log) => {
      const stepKey = log?.step_key;
      const timestamp = log?.timestamp;

      if (!stepKey || !timestamp) return acc;

      const prev = acc[stepKey];
      if (!prev || new Date(timestamp) > new Date(prev)) {
        acc[stepKey] = timestamp;
      }

      return acc;
    }, {});
  };

  const latestStepLogMap = getLatestStepLogMap(jobOrder?.report_page_logs);

  const getStepTimestamp = (stepKey) => {
    // prefer the dedicated completed_at field if present,
    // otherwise fall back to latest matching entry in report_page_logs
    return (
      jobOrder?.[`${stepKey}_completed_at`] ||
      latestStepLogMap?.[stepKey] ||
      ""
    );
  };

  const documentLinks = {
    ...buildGeneratedDocumentLinks(jobOrder?.id, {
      includeReport: true,
      includeCertificate: true,
      includeReceipt: true,
    }),
    ...(jobOrder?.reports_url && {
      reports_url: jobOrder.reports_url,
    }),
    ...(jobOrder?.certificate_url && {
      certificate_url: jobOrder.certificate_url,
    }),
    ...(jobOrder?.receipts_url && {
      receipts_url: jobOrder.receipts_url,
    }),
  };

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="View Service Report"
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid container>
        <PageOneReviewSection
          jobOrder={pageOneJobOrder}
          client={client}
          selectedJobTypes={displayedJobTypes}
          selectedJobDescriptions={displayedJobDescriptions}
          jobOrderParameters={{ status: jobOrder?.status || "" }}
          errors={{}}
          onStatusChange={() => {}}
          showStatus={true}
          readOnly={true}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.review_job_details}
              value={getStepTimestamp("review_job_details")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Inspection & Pest Count (Mobile) — wrapped in a parent card */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  Inspection Report
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              {(() => {
                const list = (locations || []).filter(
                  (loc) => loc.location && loc.location.trim()
                );
                return list.length ? (
                  <Stack spacing={2}>
                    {list.map((loc, idx) => (
                      <ViewLocationCard key={idx} loc={loc} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No inspection items
                  </Typography>
                );
              })()}
            </CardContent>

            {/* Overall Total Pests */}
            <Grid item xs={12}>              
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                      OVERALL TOTAL PESTS
                    </Typography>
                  }
                  sx={{ pb: 0, px: 2, pt: 2 }}
                />
                <CardContent sx={{ px: 2, pb: 2 }}>
                  {PEST_OPTIONS.map((p) => (
                    <Typography
                      key={p}
                      variant="body2"
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      {p}
                      <span>{overallTotals[p]}</span>
                    </Typography>
                  ))}
                  <Typography
                    variant="subtitle1"
                    sx={{ mt: 1, fontWeight: "bold", display: "flex", justifyContent: "space-between" }}
                  >
                    Total
                    <span>{grandTotal}</span>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Card>
        </Grid>
        
        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.inspection_report}
              value={getStepTimestamp("inspection_report")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Activities Done */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              title={<Typography variant="subtitle2">Activities Done</Typography>}
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                { (jobOrder.services || [])
                    .filter(s => s.activityChecked)
                    .map(s => s.activity.replace(/_/g, ' '))
                    .sort((a, b) => a.localeCompare(b))
                    .map((activity, i) => (
                  <Box
                    key={i}
                    component="li"
                    sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                  >
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{activity}</Typography>
                  </Box>
                )) }
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Services Performed */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              title={<Typography variant="subtitle2">Services Performed</Typography>}
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                { (jobOrder.services || [])
                    .filter(s => s.serviceChecked)
                    .map(s => s.service.replace(/_/g, ' '))
                    .sort((a, b) => a.localeCompare(b))
                    .map((service, i) => (
                  <Box
                    key={i}
                    component="li"
                    sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                  >
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{service}</Typography>
                  </Box>
                )) }
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Reminder Notes Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                >
                  Reminder Notes
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <TextField
                label="Reminder Notes"
                value={jobOrder.reminder_notes || ""}
                fullWidth
                multiline
                rows={6}
                size="small"
                disabled
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Chemical Used Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
              marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
              flex: 1,
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Grid item xs={12}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{ fontWeight: "bold" }}
                        >
                          Chemical Used / Batch # / Expiration
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: "bold" }}
                        >
                          Dilution
                        </TableCell>
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{ fontWeight: "bold" }}
                        >
                          Equipment Used
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(jobOrder.chemical_used || []).map((chem, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{chem.label}:</TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              value={chem.chemical || ""}
                              disabled
                              sx={{ minWidth: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              value={chem.dilution || ""}
                              disabled
                              sx={{ minWidth: 50 }}
                            />
                          </TableCell>
                          {jobOrder.equipment_used?.length > idx ? (
                            <>
                              <TableCell>
                                {jobOrder.equipment_used[idx].label}
                              </TableCell>
                              <TableCell>
                                <Checkbox
                                  checked={jobOrder.equipment_used[idx].usage}
                                  disabled
                                />
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell />
                              <TableCell />
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Control Against Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                >
                  CONTROL AGAINST
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup sx={{ width: "100%" }}>
                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={jobOrder.control_against?.crawling_insects || false}
                      disabled
                    />
                  }
                  label="Crawling Insects"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={jobOrder.control_against?.flying_insects || false}
                      disabled
                    />
                  }
                  label="Flying Insects"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={jobOrder.control_against?.rodent_control || false}
                      disabled
                    />
                  }
                  label="Rodent Control"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={jobOrder.control_against?.others || false}
                      disabled
                    />
                  }
                  label="Others"
                />
              </FormGroup>

              {jobOrder.control_against?.others && (
                <TextField
                  label="Please specify"
                  value={jobOrder.control_against?.others_text || ""}
                  fullWidth
                  size="small"
                  disabled
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.service_details}
              value={getStepTimestamp("service_details")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Payment Section */}        
        <PaymentDetailsSection
          serviceFee={jobOrder?.service_fee || ""}
          paymentStatusOptions={jobOrder?.service_report_payment_status || []}
          paymentMethodOptions={jobOrder?.service_report_payment_method || []}
          readOnly={true}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.payment_details}
              value={getStepTimestamp("payment_details")}
              colorSet="info"
            />
          </Grid>
        )}
                
        {/* Technician Signature Section */}
        <TechnicianOnlyReviewSection
          jobOrder={displayJobOrder}
          isTechnician={isTechnician}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.technician_confirmation}
              value={getStepTimestamp("technician_confirmation")}
              colorSet="info"
            />
          </Grid>
        )}
      
        {/* Client Signature Section */}
        <ClientOnlyReviewSection
          jobOrder={displayJobOrder}
          isTechnician={isTechnician}
        />

        {/* Document Links Section */}
        <DocumentLinksCard
          title="Generated Documents"
          links={documentLinks}
        />

        {/* ⏰ TIMESTAMP DISPLAY - Report Submission (HIDDEN FROM TECHNICIANS) */}
        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label="Report Submitted At"
              value={jobOrder?.report_submitted_at}
              colorSet="warning"
            />
          </Grid>
        )}

        {/* Cancel Button Section */}
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt={2}
          mb={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (returnTo) {
                // Use window.location.href to force a full page navigation
                window.location.href = returnTo;
              } else if (fromPayrollReports) {
                navigate("/payroll-reports");
              } else {
                navigate("/manage-service-reports");
              }
            }}
            sx={{
              fontSize: "0.875rem",
              color: "#FF4C4C",
              borderColor: "#FF4C4C",
            }}
            disabled={isLoading}
          >
            {fromPayrollReports ? "Back to Payroll Reports" : "Cancel"}
          </Button>
        </Grid>
      </Grid>
    </BaseFormWrapper>
  );
}

export default ViewMakatiGpcForm;
